import { useQuery } from '@apollo/client';
import { CheckCircle, Warning } from '@mui/icons-material';
import { StatusTag } from '@ovComponents/2-component/statusTag/statusTag';
import {
  Table,
  TableBody,
  TableCell,
  TableHeadCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { FlagsmithFlags } from '@utils/flagsmith';
import { Box, CircularProgress, Typography } from 'hephaestus/web';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useFeatureFlag } from '../../../../../hooks/useFeatureFlag';
import { useLocalization } from '../../../../../hooks/useLocalization';
import { AddNewForm } from './addNewForm';
import { DownloadAllForms } from './downloadAllForms';
import { FETCH_FORM_COMPLETIONS } from './manageFormCompletions.queries';
import { RegenerateForms } from './regenerateForms';
import { ViewFormDialog } from './viewFormDialog';

export const ManageFormCompletions = ({
  options,
  onNext,
  workflowCompletion,
  setActiveWorkflowCompletion,
}: {
  options: any;
  onNext: () => void;
  workflowCompletion: any;
  setActiveWorkflowCompletion?: (workflowCompletion: any) => void;
}) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation(['workflowCompletions', 'client']);
  const [activeFormCompletion, setActiveFormCompletion] = useState<any>(null);
  const showRegenerateForms = useFeatureFlag(
    FlagsmithFlags.forms_idempotent_form_steps,
  );

  // Fetch form completions for this workflow
  const { data, loading, refetch } = useQuery(FETCH_FORM_COMPLETIONS, {
    variables: {
      input: {
        filter: {
          workflowCompletionId: workflowCompletion.id,
          states: ['UNSIGNED', 'SIGNED'],
        },
        pagination: {
          perPage: 100, // Fetch all forms for this workflow
        },
      },
    },
    skip: !workflowCompletion?.id,
    fetchPolicy: 'network-only',
  });

  const formCompletions = data?.fetchFormCompletions?.formCompletions || [];

  // Separate regular forms from integration forms
  const forms = formCompletions ?? [];

  if (loading) {
    return (
      <Box
        sx={{
          height: '60vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <WorkflowCompletionForm
      onSubmit={() => onNext()}
      submitButtonProps={{
        label: t('continue'),
        disabled: loading,
        dataTestId: 'continue-button',
      }}
    >
      <Box sx={{ height: '60vh', display: 'flex', flexDirection: 'column' }}>
        {options?.title && (
          <Typography
            variant='displayLarge'
            sx={{ mt: 1, mb: options?.subtitle ? 0 : 2 }}
          >
            {translateBackendString(options?.title)}
          </Typography>
        )}
        {options?.subtitle && (
          <Typography
            variant='bodyLarge'
            component='div'
            sx={{
              mb: 3,
              table: { width: '100%' },
              display: 'inline-flex',
              alignItems: 'end',
            }}
          >
            <Box
              sx={{
                display: 'inline-block',
              }}
            >
              <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
                {translateBackendString(options?.subtitle)}
              </ReactMarkdown>
            </Box>
          </Typography>
        )}
        <Table
          sx={{
            mb: 3,
            maxHeight: '100%',
            overflow: 'auto',
            display: 'block',
          }}
        >
          <TableBody data-testid='documents-table'>
            <TableRow>
              <TableHeadCell sx={{ width: '100%' }}>
                {t('client:document')}
              </TableHeadCell>
              <TableHeadCell sx={{ minWidth: '140px' }}>
                {t('client:status')}
              </TableHeadCell>
            </TableRow>
            {forms
              .filter(
                (formCompletion: any) => formCompletion.state !== 'ARCHIVED',
              )
              .map((formCompletion: any, index: number) => (
                <TableRow
                  data-testid={`document-${index}`}
                  key={index}
                  hover
                  pointer
                  onClick={() => setActiveFormCompletion(formCompletion)}
                >
                  <TableCell>
                    {translateBackendString(
                      formCompletion.resolvedPdfFileName,
                    ) || translateBackendString(formCompletion.translatedName)}
                  </TableCell>
                  <TableCell>
                    <StatusTag
                      color={
                        formCompletion.state === 'SIGNED'
                          ? 'positive'
                          : 'warning'
                      }
                      icon={
                        formCompletion.state === 'SIGNED'
                          ? CheckCircle
                          : Warning
                      }
                      label={
                        formCompletion.state === 'SIGNED'
                          ? t('client:signed')
                          : t('client:notSigned')
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        {(options?.allowAddingNewFormCompletions ||
          showRegenerateForms ||
          options?.allowDownloadingAllForms) && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
            }}
          >
            {showRegenerateForms && (
              <RegenerateForms
                workflowCompletion={workflowCompletion}
                setActiveWorkflowCompletion={setActiveWorkflowCompletion}
              />
            )}
            {options?.allowDownloadingAllForms && (
              <DownloadAllForms
                forms={forms}
                objectId={workflowCompletion.objectId ?? ''}
                objectType={workflowCompletion.objectType}
              />
            )}
            {options?.allowAddingNewFormCompletions && (
              <AddNewForm
                workflowCompletionId={workflowCompletion.id}
                userId={workflowCompletion.objectId}
                refetch={refetch}
              />
            )}
          </Box>
        )}
        {activeFormCompletion && (
          <ViewFormDialog
            formCompletionId={activeFormCompletion.id}
            onClose={() => setActiveFormCompletion(null)}
            canArchive={options.allowRemovingFormCompletions}
          />
        )}
      </Box>
    </WorkflowCompletionForm>
  );
};
